import { type PreviewExperimentsResponse } from '../aliases';
import { getPreviewExperiments } from '../api';
import { PREVIEW_EXPERIMENTS } from '../config';
import { type CachedExperiments } from '../types';

import { getPersistentId } from '@/modules/activityTracker/helpers';
import { isServerCheck } from '@/modules/env/isServerCheck';
import { type AxiosCompatibleResponse } from '@/modules/http/types';
import { Stdv1Stat } from '@/modules/observability/constants';
import {
  getLogger,
  getUsefulErrorContents,
} from '@/modules/observability/logging';
import { recordMetric } from '@/modules/observability/metrics';
import { type User } from '@/modules/user/aliases';

function getFetchFailureCause(error: unknown) {
  if (!(error instanceof Error) || !(error.cause instanceof Error)) {
    return undefined;
  }

  const { name, message } = error.cause;

  return {
    name,
    message,
    code: 'code' in error.cause ? error.cause.code : undefined,
  };
}

async function getForwardedContentLength() {
  if (!isServerCheck()) {
    return undefined;
  }

  try {
    const { headers } = await import('next/headers');
    return (await headers()).get('content-length');
  } catch {
    return undefined;
  }
}

export async function getPopulatedPreviewExperiments(
  user?: User | null,
  countryCode?: string
) {
  // await dispatch(UserActions.getUser(request, ctx.res as any) as any);
  // only use auth in this call if we have a user in state
  const withAuth = Boolean(user && Object.keys(user).length);
  let res: AxiosCompatibleResponse<PreviewExperimentsResponse> | null = null;
  try {
    const persistentId = await getPersistentId();

    res = await getPreviewExperiments({
      countryCode: countryCode || '',
      persistentId: persistentId || '',
      withAuth,
    });

    recordMetric({
      type: 'increment',
      stat: Stdv1Stat['experiments.preview_request.prefetch.success'],
      tags: [`http.status:${res.status}`],
    });

    const { data } = res;
    // if the ua indicates a bot, we get a 202 response with no data
    // so this will handle rather than falling to the catch which was
    // filling up the logs
    const responseData = res.status === 202 ? {} : data;

    /**
     * Transforms the response to add a 'bucketed' flag to each experiment
     * as we will need to toggle these when JIT bucketing occurs
     */
    return Object.keys(responseData).reduce<CachedExperiments>((acc, val) => {
      acc[val] = {
        decision: responseData[val].variant,
        bucketed: false,
        variables: responseData[val].flag_variables,
      };
      return acc;
    }, {});
  } catch (error) {
    recordMetric({
      type: 'increment',
      stat: Stdv1Stat['experiments.preview_request.prefetch.failed'],
    });
    getLogger().warn(
      'Preview experiments prefetch failed, all decisions will fall back to control',
      {
        ...getUsefulErrorContents(
          error as Error,
          'getPopulatedPreviewExperiments'
        ),
        cause: getFetchFailureCause(error),
        forwardedContentLength: await getForwardedContentLength(),
        requestBodyBytes: new TextEncoder().encode(
          JSON.stringify(PREVIEW_EXPERIMENTS)
        ).length,
        countryCode,
        status: res?.status,
      }
    );
    return null;
  }
}
